/**
 * Écrans « Classes » et « Évaluations » de l’espace établissement
 * (`components/institution/ClassManagement.tsx`, `components/institution/EvaluationManagement.tsx`).
 *
 * Les textes fr / en de base sont dans `i18n-institution.ts` (variantes `_education` dans `i18n-education-segment.ts`).
 * Ce fichier apporte :
 * - pour fr / en : les formes plurielles (`_one` / `_other`) des messages comptés ;
 * - pour les 9 autres langues : toutes les clés de ces écrans (base « entreprise » + variante `_education`
 *   lue via le contexte de segment), fusionnées dans le bundle chargé à la demande (`lazySlices` de `lib/i18n.ts`).
 *   Les clés déjà traduites ailleurs (ex. `institution.evaluations.points*` dans `i18n-education-segment.ts`,
 *   l’arabe de `i18n-ar-education.json`) ne sont pas dupliquées.
 *
 * Pluriels i18next (CLDR) : `_one/_other` ; uk / ru : `_one/_few/_many/_other` ;
 * ar : `_zero/_one/_two/_few/_many/_other`. La clé sans suffixe reste le repli (formulation neutre).
 * Vérification : `npx vite-node scripts/check-i18n-classes-evaluations.ts`.
 */
import type { UiLanguageCode } from '@/lib/ui-languages';

export const classesEvaluationsLocales: Record<UiLanguageCode, Record<string, string>> = {
  fr: {
    'institution.classes.importPreview_one': '{{count}} élève lu dans {{file}}.',
    'institution.classes.importPreview_other': '{{count}} élèves lus dans {{file}}.',
    'institution.classes.importErrors_one': '{{count}} ligne non importée :',
    'institution.classes.importErrors_other': '{{count}} lignes non importées :',
  },
  en: {
    'institution.classes.importPreview_one': '{{count}} student read from {{file}}.',
    'institution.classes.importPreview_other': '{{count}} students read from {{file}}.',
    'institution.classes.importErrors_one': '{{count}} row not imported:',
    'institution.classes.importErrors_other': '{{count}} rows not imported:',
  },

  de: {
    // Évaluations
    'institution.evaluations.title': 'Bewertungen',
    'institution.evaluations.subtitle': 'Ergebnisse einer Klasse in einer Schulung. Umfragen und Selbsteinschätzungen zählen nicht.',
    'institution.evaluations.subtitle_education': 'Ergebnisse einer Klasse in einem Kurs. Umfragen und Selbsteinschätzungen zählen nicht.',
    'institution.evaluations.loadError': 'Die Bewertungen konnten nicht geladen werden.',
    'institution.evaluations.chooseClass': 'Klasse wählen',
    'institution.evaluations.chooseFlow': 'Schulung wählen',
    'institution.evaluations.chooseFlow_education': 'Kurs wählen',
    'institution.evaluations.emptyClass': 'Wählen Sie eine Klasse, um ihre Ergebnisse zu sehen.',
    'institution.evaluations.emptyFlow': 'Diese Klasse hat noch keine Schulung absolviert.',
    'institution.evaluations.emptyFlow_education': 'Diese Klasse hat noch keinen Kurs bearbeitet.',
    'institution.evaluations.emptyLearners': 'Diese Klasse hat noch keine Lernenden.',
    'institution.evaluations.emptyLearners_education': 'Diese Klasse hat noch keine Schülerinnen und Schüler.',
    'institution.evaluations.viewGrid': 'Tabelle',
    'institution.evaluations.viewQueue': 'Korrekturliste',
    'institution.evaluations.viewStats': 'Fragen',
    'institution.evaluations.viewSettings': 'Regeln',
    'institution.evaluations.viewCompetency': 'Kompetenzrahmen',
    'institution.evaluations.learner': 'Lernende/r',
    'institution.evaluations.learner_education': 'Schüler/in',
    'institution.evaluations.total': 'Gesamt',
    'institution.evaluations.threshold': 'Schwelle',
    'institution.evaluations.queueEmpty': 'Nichts zu korrigieren.',
    'institution.evaluations.proposal': 'Vorschlag',
    'institution.evaluations.comment': 'Kommentar für die lernende Person',
    'institution.evaluations.comment_education': 'Kommentar für die Schülerin / den Schüler',
    'institution.evaluations.validate': 'Bestätigen',
    'institution.evaluations.passPercent': 'Bestehensgrenze (%)',
    'institution.evaluations.attemptRule': 'Gewerteter Versuch',
    'institution.evaluations.ruleLast': 'Letzter Versuch',
    'institution.evaluations.ruleBest': 'Bester Versuch',
    'institution.evaluations.noSuggestion': 'Keine Höherstufung vorzuschlagen.',
    'institution.evaluations.applyLevel': 'Höherstufung übernehmen',
    'institution.evaluations.openFromClass': 'Bewertungen anzeigen',
    'institution.evaluations.status.passed': 'Bestanden',
    'institution.evaluations.status.failed': 'Nicht bestanden',
    'institution.evaluations.status.toReview': 'In Korrektur',
    'institution.evaluations.status.inProgress': 'In Bearbeitung',
    'institution.evaluations.status.notStarted': 'Nicht begonnen',
    'institution.evaluations.status.counted': 'Gewertet',
    'institution.evaluations.signal.easy': 'Zu leicht',
    'institution.evaluations.signal.hard': 'Zu schwer',
    'institution.evaluations.signal.balanced': 'Ausgewogen',
    'institution.evaluations.signal.insufficient': 'Zu wenige Antworten',
    // Classes
    'institution.classes.title': 'Klassen',
    'institution.classes.subtitle': 'Eine Klasse hat eine verantwortliche Person, Trainer und Lernende. Importieren Sie die Teilnehmerliste und weisen Sie allen auf einmal Schulungen zu.',
    'institution.classes.subtitle_education': 'Eine Klasse hat eine Klassenlehrkraft, Lehrkräfte sowie Schülerinnen und Schüler. Importieren Sie die Schülerliste und weisen Sie allen auf einmal Kurse zu.',
    'institution.classes.create': 'Klasse erstellen',
    'institution.classes.loadError': 'Die Klassen konnten nicht geladen werden.',
    'institution.classes.saveError': 'Speichern nicht möglich.',
    'institution.classes.search': 'Klasse suchen…',
    'institution.classes.empty': 'Noch keine Klasse. Erstellen Sie die erste auf dieser Seite.',
    'institution.classes.emptyFilter': 'Keine Klasse entspricht der Suche.',
    'institution.classes.colName': 'Klasse',
    'institution.classes.lead': 'Klassenverantwortliche/r',
    'institution.classes.lead_education': 'Klassenlehrkraft',
    'institution.classes.leadPlaceholder': 'Klassenverantwortliche/n wählen',
    'institution.classes.leadPlaceholder_education': 'Klassenlehrkraft wählen',
    'institution.classes.leadHint': 'Die verantwortliche Person betreut die Klasse (Klassenlehrkraft, Koordination oder Leitung). Die Trainer unterrichten dort ihre Fächer.',
    'institution.classes.leadHint_education': 'Die Klassenlehrkraft betreut die Klasse (oder eine Koordinatorin, ein Mitglied der Schulleitung). Die Lehrkräfte unterrichten dort ihre Fächer.',
    'institution.classes.leadMissing': 'Keine verantwortliche Person',
    'institution.classes.leadMissing_education': 'Keine Klassenlehrkraft',
    'institution.classes.noLead': 'Legen Sie zuerst unter „Mitarbeitende“ ein Konto als Trainer, Schulungsverantwortliche/r oder Administrator an.',
    'institution.classes.noLead_education': 'Legen Sie zuerst unter „Lehrkräfte“ ein Konto als Lehrkraft, Klassenlehrkraft oder Administrator an.',
    'institution.classes.trainers': 'Trainer',
    'institution.classes.trainers_education': 'Lehrkräfte',
    'institution.classes.learners': 'Lernende',
    'institution.classes.learners_education': 'Schüler',
    'institution.classes.addTrainer': 'Trainer hinzufügen',
    'institution.classes.addTrainer_education': 'Lehrkraft hinzufügen',
    'institution.classes.addLearner': 'Lernende/n hinzufügen',
    'institution.classes.addLearner_education': 'Schüler/in hinzufügen',
    'institution.classes.noTrainers': 'Kein Trainer in dieser Klasse.',
    'institution.classes.noTrainers_education': 'Keine Lehrkraft in dieser Klasse.',
    'institution.classes.noLearners': 'Keine Lernenden in dieser Klasse.',
    'institution.classes.noLearners_education': 'Keine Schülerinnen und Schüler in dieser Klasse.',
    'institution.classes.noTrainerAvailable': 'Kein Trainer verfügbar.',
    'institution.classes.noTrainerAvailable_education': 'Keine Lehrkraft verfügbar.',
    'institution.classes.noLearnerAvailable': 'Keine Lernenden verfügbar.',
    'institution.classes.noLearnerAvailable_education': 'Keine Schülerinnen und Schüler verfügbar.',
    'institution.classes.searchPeople': 'Suchen…',
    'institution.classes.back': 'Zurück zu den Klassen',
    'institution.classes.remove': 'Entfernen',
    'institution.classes.modalCreate': 'Klasse erstellen',
    'institution.classes.modalEdit': 'Klasse bearbeiten',
    'institution.classes.fieldName': 'Name der Klasse',
    'institution.classes.confirmDeleteTitle': 'Diese Klasse löschen?',
    'institution.classes.confirmDeleteBody': 'Die Klasse „{{name}}“ wird gelöscht. Die Konten bleiben erhalten.',
    'institution.classes.confirmRemoveTitle': 'Diese Person entfernen?',
    'institution.classes.confirmRemoveBody': '{{name}} wird aus der Klasse entfernt. Das Konto bleibt erhalten.',
    'institution.classes.flowsTitle': 'Schulungen der Klasse',
    'institution.classes.flowsTitle_education': 'Kurse der Klasse',
    'institution.classes.flowsHint': 'Weisen Sie der ganzen Klasse eine veröffentlichte Schulung zu. Bereits eingeschriebene Personen werden nicht doppelt angelegt.',
    'institution.classes.flowsHint_education': 'Weisen Sie der ganzen Klasse einen veröffentlichten Kurs zu. Bereits eingeschriebene Schülerinnen und Schüler werden nicht doppelt angelegt.',
    'institution.classes.chooseFlow': 'Veröffentlichte Schulung wählen',
    'institution.classes.chooseFlow_education': 'Veröffentlichten Kurs wählen',
    'institution.classes.assignFlow': 'Der Klasse zuweisen',
    'institution.classes.assignDone': 'Eingeschrieben: {{assigned}} · bereits eingeschrieben: {{already}}.',
    'institution.classes.noFlows': 'Für diese Einrichtung ist keine veröffentlichte Schulung verfügbar.',
    'institution.classes.noFlows_education': 'Für diese Schule ist kein veröffentlichter Kurs verfügbar.',
    'institution.classes.enrolledCount': '{{enrolled}} / {{total}} eingeschrieben',
    'institution.classes.notifyStudents': 'Neu eingeschriebene Schülerinnen und Schüler per E-Mail benachrichtigen',
    'institution.classes.importCsv': 'Importieren (CSV)',
    'institution.classes.importTitle': 'Schülerinnen und Schüler in die Klasse importieren',
    'institution.classes.importHelp': 'CSV-Datei mit drei Spalten: Vorname, Nachname, E-Mail (Trennzeichen „;“ oder „,“). Höchstens {{max}} Zeilen. Fehlende Konten werden als Schülerkonten angelegt.',
    'institution.classes.importTemplate': 'Vorlage herunterladen',
    'institution.classes.importFile': 'CSV-Datei',
    'institution.classes.importPreview': 'Aus {{file}} gelesene Schülerinnen und Schüler: {{count}}.',
    'institution.classes.importPreview_one': '{{count}} Schülerin/Schüler aus {{file}} gelesen.',
    'institution.classes.importPreview_other': '{{count}} Schülerinnen und Schüler aus {{file}} gelesen.',
    'institution.classes.importSkipped': 'Ohne E-Mail übersprungene Zeilen: {{lines}}',
    'institution.classes.importInvite': 'Jedem neuen Konto eine E-Mail senden, um ein Passwort festzulegen',
    'institution.classes.importRun': 'Importieren',
    'institution.classes.importResult': 'Zur Klasse hinzugefügt: {{added}} · neu angelegte Konten: {{created}} · bereits in der Klasse: {{already}} · Einladungen im Versand: {{invited}}.',
    'institution.classes.importErrors': 'Nicht importierte Zeilen: {{count}}',
    'institution.classes.importErrors_one': '{{count}} Zeile nicht importiert:',
    'institution.classes.importErrors_other': '{{count}} Zeilen nicht importiert:',
    'institution.classes.importErrorLine': 'Zeile {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'In dieser Datei wurden keine Schülerinnen und Schüler gefunden.',
    'institution.classes.importTooMany': 'Die Datei hat mehr als {{max}} Zeilen: Teilen Sie sie auf.',
    'institution.classes.importReadError': 'Die Datei konnte nicht gelesen werden.',
    'institution.classes.close': 'Schließen',
  },

  es: {
    // Évaluations
    'institution.evaluations.title': 'Evaluaciones',
    'institution.evaluations.subtitle': 'Resultados de una clase en una formación. Las encuestas y la autoevaluación no cuentan.',
    'institution.evaluations.subtitle_education': 'Resultados de una clase en un curso. Las encuestas y la autoevaluación no cuentan.',
    'institution.evaluations.loadError': 'No se pudieron cargar las evaluaciones.',
    'institution.evaluations.chooseClass': 'Elegir una clase',
    'institution.evaluations.chooseFlow': 'Elegir una formación',
    'institution.evaluations.chooseFlow_education': 'Elegir un curso',
    'institution.evaluations.emptyClass': 'Elija una clase para ver sus resultados.',
    'institution.evaluations.emptyFlow': 'Esta clase todavía no ha realizado ninguna formación.',
    'institution.evaluations.emptyFlow_education': 'Esta clase todavía no ha realizado ningún curso.',
    'institution.evaluations.emptyLearners': 'Esta clase todavía no tiene participantes.',
    'institution.evaluations.emptyLearners_education': 'Esta clase todavía no tiene alumnos.',
    'institution.evaluations.viewGrid': 'Tabla',
    'institution.evaluations.viewQueue': 'Cola de corrección',
    'institution.evaluations.viewStats': 'Preguntas',
    'institution.evaluations.viewSettings': 'Reglas',
    'institution.evaluations.viewCompetency': 'Marco de competencias',
    'institution.evaluations.learner': 'Participante',
    'institution.evaluations.learner_education': 'Alumno',
    'institution.evaluations.total': 'Total',
    'institution.evaluations.threshold': 'Umbral',
    'institution.evaluations.queueEmpty': 'Nada que corregir.',
    'institution.evaluations.proposal': 'Propuesta',
    'institution.evaluations.comment': 'Comentario para el participante',
    'institution.evaluations.comment_education': 'Comentario para el alumno',
    'institution.evaluations.validate': 'Validar',
    'institution.evaluations.passPercent': 'Umbral de aprobado (%)',
    'institution.evaluations.attemptRule': 'Intento que cuenta',
    'institution.evaluations.ruleLast': 'Último intento',
    'institution.evaluations.ruleBest': 'Mejor intento',
    'institution.evaluations.noSuggestion': 'No hay ninguna subida de nivel que proponer.',
    'institution.evaluations.applyLevel': 'Aplicar la subida de nivel',
    'institution.evaluations.openFromClass': 'Ver las evaluaciones',
    'institution.evaluations.status.passed': 'Aprobado',
    'institution.evaluations.status.failed': 'No aprobado',
    'institution.evaluations.status.toReview': 'En corrección',
    'institution.evaluations.status.inProgress': 'En curso',
    'institution.evaluations.status.notStarted': 'Sin empezar',
    'institution.evaluations.status.counted': 'Computado',
    'institution.evaluations.signal.easy': 'Demasiado fácil',
    'institution.evaluations.signal.hard': 'Demasiado difícil',
    'institution.evaluations.signal.balanced': 'Equilibrada',
    'institution.evaluations.signal.insufficient': 'Respuestas insuficientes',
    // Classes
    'institution.classes.title': 'Clases',
    'institution.classes.subtitle': 'Una clase tiene un responsable, formadores y alumnos. Importe la lista de alumnos y asígneles formaciones de una sola vez.',
    'institution.classes.subtitle_education': 'Una clase tiene un tutor, profesores y alumnos. Importe la lista de alumnos y asígneles cursos de una sola vez.',
    'institution.classes.create': 'Crear una clase',
    'institution.classes.loadError': 'No se pudieron cargar las clases.',
    'institution.classes.saveError': 'No se pudo guardar.',
    'institution.classes.search': 'Buscar una clase…',
    'institution.classes.empty': 'Todavía no hay ninguna clase. Cree la primera desde esta pantalla.',
    'institution.classes.emptyFilter': 'Ninguna clase coincide con la búsqueda.',
    'institution.classes.colName': 'Clase',
    'institution.classes.lead': 'Responsable de clase',
    'institution.classes.lead_education': 'Tutor',
    'institution.classes.leadPlaceholder': 'Elegir un responsable de clase',
    'institution.classes.leadPlaceholder_education': 'Elegir un tutor',
    'institution.classes.leadHint': 'El responsable sigue la clase (tutor, coordinador o dirección). Los formadores imparten en ella sus materias.',
    'institution.classes.leadHint_education': 'El tutor sigue la clase (o un coordinador, un miembro del equipo directivo). Los profesores imparten en ella sus asignaturas.',
    'institution.classes.leadMissing': 'Sin responsable',
    'institution.classes.leadMissing_education': 'Sin tutor',
    'institution.classes.noLead': 'Primero cree una cuenta de formador, responsable de formación o administrador en Colaboradores.',
    'institution.classes.noLead_education': 'Primero cree una cuenta de profesor, tutor o administrador en Profesores.',
    'institution.classes.trainers': 'Formadores',
    'institution.classes.trainers_education': 'Profesores',
    'institution.classes.learners': 'Participantes',
    'institution.classes.learners_education': 'Alumnos',
    'institution.classes.addTrainer': 'Añadir un formador',
    'institution.classes.addTrainer_education': 'Añadir un profesor',
    'institution.classes.addLearner': 'Añadir un participante',
    'institution.classes.addLearner_education': 'Añadir un alumno',
    'institution.classes.noTrainers': 'No hay ningún formador en esta clase.',
    'institution.classes.noTrainers_education': 'No hay ningún profesor en esta clase.',
    'institution.classes.noLearners': 'No hay ningún participante en esta clase.',
    'institution.classes.noLearners_education': 'No hay ningún alumno en esta clase.',
    'institution.classes.noTrainerAvailable': 'No hay ningún formador disponible.',
    'institution.classes.noTrainerAvailable_education': 'No hay ningún profesor disponible.',
    'institution.classes.noLearnerAvailable': 'No hay ningún participante disponible.',
    'institution.classes.noLearnerAvailable_education': 'No hay ningún alumno disponible.',
    'institution.classes.searchPeople': 'Buscar…',
    'institution.classes.back': 'Volver a las clases',
    'institution.classes.remove': 'Quitar',
    'institution.classes.modalCreate': 'Crear una clase',
    'institution.classes.modalEdit': 'Editar la clase',
    'institution.classes.fieldName': 'Nombre de la clase',
    'institution.classes.confirmDeleteTitle': '¿Eliminar esta clase?',
    'institution.classes.confirmDeleteBody': 'La clase «{{name}}» se eliminará. Las cuentas se conservan.',
    'institution.classes.confirmRemoveTitle': '¿Quitar a esta persona?',
    'institution.classes.confirmRemoveBody': '{{name}} será retirado de la clase. La cuenta se conserva.',
    'institution.classes.flowsTitle': 'Formaciones de la clase',
    'institution.classes.flowsTitle_education': 'Cursos de la clase',
    'institution.classes.flowsHint': 'Asigne una formación publicada a toda la clase. Los alumnos ya inscritos no se duplican.',
    'institution.classes.flowsHint_education': 'Asigne un curso publicado a toda la clase. Los alumnos ya inscritos no se duplican.',
    'institution.classes.chooseFlow': 'Elegir una formación publicada',
    'institution.classes.chooseFlow_education': 'Elegir un curso publicado',
    'institution.classes.assignFlow': 'Asignar a la clase',
    'institution.classes.assignDone': 'Alumnos inscritos: {{assigned}} · ya inscritos: {{already}}.',
    'institution.classes.noFlows': 'No hay ninguna formación publicada disponible para este centro.',
    'institution.classes.noFlows_education': 'No hay ningún curso publicado disponible para este centro.',
    'institution.classes.enrolledCount': '{{enrolled}} / {{total}} inscritos',
    'institution.classes.notifyStudents': 'Avisar por correo electrónico a los alumnos recién inscritos',
    'institution.classes.importCsv': 'Importar (CSV)',
    'institution.classes.importTitle': 'Importar alumnos a la clase',
    'institution.classes.importHelp': 'Archivo CSV con tres columnas: nombre, apellidos, correo electrónico (separador «;» o «,»). Máximo {{max}} filas. Las cuentas que falten se crean como cuentas de alumno.',
    'institution.classes.importTemplate': 'Descargar la plantilla',
    'institution.classes.importFile': 'Archivo CSV',
    'institution.classes.importPreview': 'Alumnos leídos en {{file}}: {{count}}.',
    'institution.classes.importPreview_one': '{{count}} alumno leído en {{file}}.',
    'institution.classes.importPreview_other': '{{count}} alumnos leídos en {{file}}.',
    'institution.classes.importSkipped': 'Filas ignoradas por falta de correo electrónico: {{lines}}',
    'institution.classes.importInvite': 'Enviar a cada cuenta nueva un correo para elegir su contraseña',
    'institution.classes.importRun': 'Importar',
    'institution.classes.importResult': 'Añadidos a la clase: {{added}} · cuentas creadas: {{created}} · ya en la clase: {{already}} · invitaciones en curso de envío: {{invited}}.',
    'institution.classes.importErrors': 'Filas no importadas: {{count}}',
    'institution.classes.importErrors_one': '{{count}} fila no importada:',
    'institution.classes.importErrors_other': '{{count}} filas no importadas:',
    'institution.classes.importErrorLine': 'Fila {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'No se ha encontrado ningún alumno en este archivo.',
    'institution.classes.importTooMany': 'El archivo supera las {{max}} filas: divídalo.',
    'institution.classes.importReadError': 'No se pudo leer el archivo.',
    'institution.classes.close': 'Cerrar',
  },

  it: {
    // Évaluations
    'institution.evaluations.title': 'Valutazioni',
    'institution.evaluations.subtitle': 'Risultati di una classe in un corso di formazione. I sondaggi e l’autovalutazione non contano.',
    'institution.evaluations.subtitle_education': 'Risultati di una classe in un corso. I sondaggi e l’autovalutazione non contano.',
    'institution.evaluations.loadError': 'Impossibile caricare le valutazioni.',
    'institution.evaluations.chooseClass': 'Scegli una classe',
    'institution.evaluations.chooseFlow': 'Scegli una formazione',
    'institution.evaluations.chooseFlow_education': 'Scegli un corso',
    'institution.evaluations.emptyClass': 'Scegli una classe per vederne i risultati.',
    'institution.evaluations.emptyFlow': 'Questa classe non ha ancora svolto nessuna formazione.',
    'institution.evaluations.emptyFlow_education': 'Questa classe non ha ancora svolto nessun corso.',
    'institution.evaluations.emptyLearners': 'Questa classe non ha ancora allievi.',
    'institution.evaluations.emptyLearners_education': 'Questa classe non ha ancora studenti.',
    'institution.evaluations.viewGrid': 'Tabella',
    'institution.evaluations.viewQueue': 'Coda di correzione',
    'institution.evaluations.viewStats': 'Domande',
    'institution.evaluations.viewSettings': 'Regole',
    'institution.evaluations.viewCompetency': 'Quadro delle competenze',
    'institution.evaluations.learner': 'Allievo',
    'institution.evaluations.learner_education': 'Studente',
    'institution.evaluations.total': 'Totale',
    'institution.evaluations.threshold': 'Soglia',
    'institution.evaluations.queueEmpty': 'Niente da correggere.',
    'institution.evaluations.proposal': 'Proposta',
    'institution.evaluations.comment': 'Commento per l’allievo',
    'institution.evaluations.comment_education': 'Commento per lo studente',
    'institution.evaluations.validate': 'Convalida',
    'institution.evaluations.passPercent': 'Soglia di superamento (%)',
    'institution.evaluations.attemptRule': 'Tentativo considerato',
    'institution.evaluations.ruleLast': 'Ultimo tentativo',
    'institution.evaluations.ruleBest': 'Tentativo migliore',
    'institution.evaluations.noSuggestion': 'Nessun passaggio di livello da proporre.',
    'institution.evaluations.applyLevel': 'Applica il passaggio di livello',
    'institution.evaluations.openFromClass': 'Vedi le valutazioni',
    'institution.evaluations.status.passed': 'Superato',
    'institution.evaluations.status.failed': 'Non superato',
    'institution.evaluations.status.toReview': 'In correzione',
    'institution.evaluations.status.inProgress': 'In corso',
    'institution.evaluations.status.notStarted': 'Non iniziato',
    'institution.evaluations.status.counted': 'Conteggiato',
    'institution.evaluations.signal.easy': 'Troppo facile',
    'institution.evaluations.signal.hard': 'Troppo difficile',
    'institution.evaluations.signal.balanced': 'Equilibrata',
    'institution.evaluations.signal.insufficient': 'Risposte insufficienti',
    // Classes
    'institution.classes.title': 'Classi',
    'institution.classes.subtitle': 'Una classe ha un responsabile, dei formatori e degli allievi. Importa l’elenco degli allievi e assegna loro le formazioni in una sola volta.',
    'institution.classes.subtitle_education': 'Una classe ha un coordinatore di classe, dei docenti e degli studenti. Importa l’elenco degli studenti e assegna loro i corsi in una sola volta.',
    'institution.classes.create': 'Crea una classe',
    'institution.classes.loadError': 'Impossibile caricare le classi.',
    'institution.classes.saveError': 'Salvataggio non riuscito.',
    'institution.classes.search': 'Cerca una classe…',
    'institution.classes.empty': 'Ancora nessuna classe. Crea la prima da questa schermata.',
    'institution.classes.emptyFilter': 'Nessuna classe corrisponde alla ricerca.',
    'institution.classes.colName': 'Classe',
    'institution.classes.lead': 'Responsabile della classe',
    'institution.classes.lead_education': 'Coordinatore di classe',
    'institution.classes.leadPlaceholder': 'Scegli un responsabile della classe',
    'institution.classes.leadPlaceholder_education': 'Scegli un coordinatore di classe',
    'institution.classes.leadHint': 'Il responsabile segue la classe (coordinatore, referente o direzione). I formatori vi insegnano le loro materie.',
    'institution.classes.leadHint_education': 'Il coordinatore di classe segue la classe (oppure un referente o un membro della dirigenza). I docenti vi insegnano le loro materie.',
    'institution.classes.leadMissing': 'Nessun responsabile',
    'institution.classes.leadMissing_education': 'Nessun coordinatore di classe',
    'institution.classes.noLead': 'Crea prima un account formatore, responsabile della formazione o amministratore in Collaboratori.',
    'institution.classes.noLead_education': 'Crea prima un account docente, coordinatore di classe o amministratore in Docenti.',
    'institution.classes.trainers': 'Formatori',
    'institution.classes.trainers_education': 'Docenti',
    'institution.classes.learners': 'Allievi',
    'institution.classes.learners_education': 'Studenti',
    'institution.classes.addTrainer': 'Aggiungi un formatore',
    'institution.classes.addTrainer_education': 'Aggiungi un docente',
    'institution.classes.addLearner': 'Aggiungi un allievo',
    'institution.classes.addLearner_education': 'Aggiungi uno studente',
    'institution.classes.noTrainers': 'Nessun formatore in questa classe.',
    'institution.classes.noTrainers_education': 'Nessun docente in questa classe.',
    'institution.classes.noLearners': 'Nessun allievo in questa classe.',
    'institution.classes.noLearners_education': 'Nessuno studente in questa classe.',
    'institution.classes.noTrainerAvailable': 'Nessun formatore disponibile.',
    'institution.classes.noTrainerAvailable_education': 'Nessun docente disponibile.',
    'institution.classes.noLearnerAvailable': 'Nessun allievo disponibile.',
    'institution.classes.noLearnerAvailable_education': 'Nessuno studente disponibile.',
    'institution.classes.searchPeople': 'Cerca…',
    'institution.classes.back': 'Torna alle classi',
    'institution.classes.remove': 'Rimuovi',
    'institution.classes.modalCreate': 'Crea una classe',
    'institution.classes.modalEdit': 'Modifica la classe',
    'institution.classes.fieldName': 'Nome della classe',
    'institution.classes.confirmDeleteTitle': 'Eliminare questa classe?',
    'institution.classes.confirmDeleteBody': 'La classe «{{name}}» verrà eliminata. Gli account vengono conservati.',
    'institution.classes.confirmRemoveTitle': 'Rimuovere questa persona?',
    'institution.classes.confirmRemoveBody': '{{name}} verrà rimosso dalla classe. L’account viene conservato.',
    'institution.classes.flowsTitle': 'Formazioni della classe',
    'institution.classes.flowsTitle_education': 'Corsi della classe',
    'institution.classes.flowsHint': 'Assegna una formazione pubblicata a tutta la classe. Gli allievi già iscritti non vengono duplicati.',
    'institution.classes.flowsHint_education': 'Assegna un corso pubblicato a tutta la classe. Gli studenti già iscritti non vengono duplicati.',
    'institution.classes.chooseFlow': 'Scegli una formazione pubblicata',
    'institution.classes.chooseFlow_education': 'Scegli un corso pubblicato',
    'institution.classes.assignFlow': 'Assegna alla classe',
    'institution.classes.assignDone': 'Iscritti: {{assigned}} · già iscritti: {{already}}.',
    'institution.classes.noFlows': 'Nessuna formazione pubblicata disponibile per questo istituto.',
    'institution.classes.noFlows_education': 'Nessun corso pubblicato disponibile per questa scuola.',
    'institution.classes.enrolledCount': '{{enrolled}} / {{total}} iscritti',
    'institution.classes.notifyStudents': 'Avvisa via e-mail gli studenti appena iscritti',
    'institution.classes.importCsv': 'Importa (CSV)',
    'institution.classes.importTitle': 'Importa studenti nella classe',
    'institution.classes.importHelp': 'File CSV con tre colonne: nome, cognome, e-mail (separatore «;» o «,»). Massimo {{max}} righe. Gli account mancanti vengono creati come account studente.',
    'institution.classes.importTemplate': 'Scarica il modello',
    'institution.classes.importFile': 'File CSV',
    'institution.classes.importPreview': 'Studenti letti da {{file}}: {{count}}.',
    'institution.classes.importPreview_one': '{{count}} studente letto da {{file}}.',
    'institution.classes.importPreview_other': '{{count}} studenti letti da {{file}}.',
    'institution.classes.importSkipped': 'Righe ignorate perché senza e-mail: {{lines}}',
    'institution.classes.importInvite': 'Invia a ogni nuovo account un’e-mail per scegliere la password',
    'institution.classes.importRun': 'Importa',
    'institution.classes.importResult': 'Aggiunti alla classe: {{added}} · account creati: {{created}} · già nella classe: {{already}} · inviti in fase di invio: {{invited}}.',
    'institution.classes.importErrors': 'Righe non importate: {{count}}',
    'institution.classes.importErrors_one': '{{count}} riga non importata:',
    'institution.classes.importErrors_other': '{{count}} righe non importate:',
    'institution.classes.importErrorLine': 'Riga {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'Nessuno studente trovato in questo file.',
    'institution.classes.importTooMany': 'Il file supera le {{max}} righe: dividilo.',
    'institution.classes.importReadError': 'Impossibile leggere il file.',
    'institution.classes.close': 'Chiudi',
  },

  pt: {
    // Évaluations
    'institution.evaluations.title': 'Avaliações',
    'institution.evaluations.subtitle': 'Resultados de uma turma numa formação. Os inquéritos e a autoavaliação não contam.',
    'institution.evaluations.subtitle_education': 'Resultados de uma turma num curso. Os inquéritos e a autoavaliação não contam.',
    'institution.evaluations.loadError': 'Não foi possível carregar as avaliações.',
    'institution.evaluations.chooseClass': 'Escolher uma turma',
    'institution.evaluations.chooseFlow': 'Escolher uma formação',
    'institution.evaluations.chooseFlow_education': 'Escolher um curso',
    'institution.evaluations.emptyClass': 'Escolha uma turma para ver os resultados.',
    'institution.evaluations.emptyFlow': 'Esta turma ainda não fez nenhuma formação.',
    'institution.evaluations.emptyFlow_education': 'Esta turma ainda não fez nenhum curso.',
    'institution.evaluations.emptyLearners': 'Esta turma ainda não tem formandos.',
    'institution.evaluations.emptyLearners_education': 'Esta turma ainda não tem alunos.',
    'institution.evaluations.viewGrid': 'Tabela',
    'institution.evaluations.viewQueue': 'Fila de correção',
    'institution.evaluations.viewStats': 'Perguntas',
    'institution.evaluations.viewSettings': 'Regras',
    'institution.evaluations.viewCompetency': 'Referencial de competências',
    'institution.evaluations.learner': 'Formando',
    'institution.evaluations.learner_education': 'Aluno',
    'institution.evaluations.total': 'Total',
    'institution.evaluations.threshold': 'Limiar',
    'institution.evaluations.queueEmpty': 'Nada para corrigir.',
    'institution.evaluations.proposal': 'Proposta',
    'institution.evaluations.comment': 'Comentário para o formando',
    'institution.evaluations.comment_education': 'Comentário para o aluno',
    'institution.evaluations.validate': 'Validar',
    'institution.evaluations.passPercent': 'Limiar de aprovação (%)',
    'institution.evaluations.attemptRule': 'Tentativa considerada',
    'institution.evaluations.ruleLast': 'Última tentativa',
    'institution.evaluations.ruleBest': 'Melhor tentativa',
    'institution.evaluations.noSuggestion': 'Nenhuma subida de nível a propor.',
    'institution.evaluations.applyLevel': 'Aplicar a subida de nível',
    'institution.evaluations.openFromClass': 'Ver as avaliações',
    'institution.evaluations.status.passed': 'Aprovado',
    'institution.evaluations.status.failed': 'Não aprovado',
    'institution.evaluations.status.toReview': 'Em correção',
    'institution.evaluations.status.inProgress': 'Em curso',
    'institution.evaluations.status.notStarted': 'Não iniciado',
    'institution.evaluations.status.counted': 'Contabilizado',
    'institution.evaluations.signal.easy': 'Demasiado fácil',
    'institution.evaluations.signal.hard': 'Demasiado difícil',
    'institution.evaluations.signal.balanced': 'Equilibrada',
    'institution.evaluations.signal.insufficient': 'Respostas insuficientes',
    // Classes
    'institution.classes.title': 'Turmas',
    'institution.classes.subtitle': 'Uma turma tem um responsável, formadores e alunos. Importe a lista de alunos e atribua-lhes formações de uma só vez.',
    'institution.classes.subtitle_education': 'Uma turma tem um diretor de turma, professores e alunos. Importe a lista de alunos e atribua-lhes cursos de uma só vez.',
    'institution.classes.create': 'Criar uma turma',
    'institution.classes.loadError': 'Não foi possível carregar as turmas.',
    'institution.classes.saveError': 'Não foi possível guardar.',
    'institution.classes.search': 'Pesquisar uma turma…',
    'institution.classes.empty': 'Ainda não há turmas. Crie a primeira a partir deste ecrã.',
    'institution.classes.emptyFilter': 'Nenhuma turma corresponde à pesquisa.',
    'institution.classes.colName': 'Turma',
    'institution.classes.lead': 'Responsável da turma',
    'institution.classes.lead_education': 'Diretor de turma',
    'institution.classes.leadPlaceholder': 'Escolher um responsável da turma',
    'institution.classes.leadPlaceholder_education': 'Escolher um diretor de turma',
    'institution.classes.leadHint': 'O responsável acompanha a turma (diretor de turma, coordenador ou direção). Os formadores lecionam nela as suas disciplinas.',
    'institution.classes.leadHint_education': 'O diretor de turma acompanha a turma (ou um coordenador, um membro da direção). Os professores lecionam nela as suas disciplinas.',
    'institution.classes.leadMissing': 'Sem responsável',
    'institution.classes.leadMissing_education': 'Sem diretor de turma',
    'institution.classes.noLead': 'Crie primeiro uma conta de formador, responsável de formação ou administrador em Colaboradores.',
    'institution.classes.noLead_education': 'Crie primeiro uma conta de professor, diretor de turma ou administrador em Professores.',
    'institution.classes.trainers': 'Formadores',
    'institution.classes.trainers_education': 'Professores',
    'institution.classes.learners': 'Formandos',
    'institution.classes.learners_education': 'Alunos',
    'institution.classes.addTrainer': 'Adicionar um formador',
    'institution.classes.addTrainer_education': 'Adicionar um professor',
    'institution.classes.addLearner': 'Adicionar um formando',
    'institution.classes.addLearner_education': 'Adicionar um aluno',
    'institution.classes.noTrainers': 'Nenhum formador nesta turma.',
    'institution.classes.noTrainers_education': 'Nenhum professor nesta turma.',
    'institution.classes.noLearners': 'Nenhum formando nesta turma.',
    'institution.classes.noLearners_education': 'Nenhum aluno nesta turma.',
    'institution.classes.noTrainerAvailable': 'Nenhum formador disponível.',
    'institution.classes.noTrainerAvailable_education': 'Nenhum professor disponível.',
    'institution.classes.noLearnerAvailable': 'Nenhum formando disponível.',
    'institution.classes.noLearnerAvailable_education': 'Nenhum aluno disponível.',
    'institution.classes.searchPeople': 'Pesquisar…',
    'institution.classes.back': 'Voltar às turmas',
    'institution.classes.remove': 'Retirar',
    'institution.classes.modalCreate': 'Criar uma turma',
    'institution.classes.modalEdit': 'Editar a turma',
    'institution.classes.fieldName': 'Nome da turma',
    'institution.classes.confirmDeleteTitle': 'Eliminar esta turma?',
    'institution.classes.confirmDeleteBody': 'A turma «{{name}}» será eliminada. As contas são mantidas.',
    'institution.classes.confirmRemoveTitle': 'Retirar esta pessoa?',
    'institution.classes.confirmRemoveBody': '{{name}} será retirado da turma. A conta é mantida.',
    'institution.classes.flowsTitle': 'Formações da turma',
    'institution.classes.flowsTitle_education': 'Cursos da turma',
    'institution.classes.flowsHint': 'Atribua uma formação publicada a toda a turma. Os alunos já inscritos não são duplicados.',
    'institution.classes.flowsHint_education': 'Atribua um curso publicado a toda a turma. Os alunos já inscritos não são duplicados.',
    'institution.classes.chooseFlow': 'Escolher uma formação publicada',
    'institution.classes.chooseFlow_education': 'Escolher um curso publicado',
    'institution.classes.assignFlow': 'Atribuir à turma',
    'institution.classes.assignDone': 'Alunos inscritos: {{assigned}} · já inscritos: {{already}}.',
    'institution.classes.noFlows': 'Nenhuma formação publicada disponível para esta instituição.',
    'institution.classes.noFlows_education': 'Nenhum curso publicado disponível para esta escola.',
    'institution.classes.enrolledCount': '{{enrolled}} / {{total}} inscritos',
    'institution.classes.notifyStudents': 'Avisar por e-mail os alunos recém-inscritos',
    'institution.classes.importCsv': 'Importar (CSV)',
    'institution.classes.importTitle': 'Importar alunos para a turma',
    'institution.classes.importHelp': 'Ficheiro CSV com três colunas: nome próprio, apelido, e-mail (separador «;» ou «,»). Máximo de {{max}} linhas. As contas em falta são criadas como contas de aluno.',
    'institution.classes.importTemplate': 'Transferir o modelo',
    'institution.classes.importFile': 'Ficheiro CSV',
    'institution.classes.importPreview': 'Alunos lidos em {{file}}: {{count}}.',
    'institution.classes.importPreview_one': '{{count}} aluno lido em {{file}}.',
    'institution.classes.importPreview_other': '{{count}} alunos lidos em {{file}}.',
    'institution.classes.importSkipped': 'Linhas ignoradas por falta de e-mail: {{lines}}',
    'institution.classes.importInvite': 'Enviar a cada nova conta um e-mail para escolher a palavra-passe',
    'institution.classes.importRun': 'Importar',
    'institution.classes.importResult': 'Adicionados à turma: {{added}} · contas criadas: {{created}} · já na turma: {{already}} · convites a ser enviados: {{invited}}.',
    'institution.classes.importErrors': 'Linhas não importadas: {{count}}',
    'institution.classes.importErrors_one': '{{count}} linha não importada:',
    'institution.classes.importErrors_other': '{{count}} linhas não importadas:',
    'institution.classes.importErrorLine': 'Linha {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'Nenhum aluno encontrado neste ficheiro.',
    'institution.classes.importTooMany': 'O ficheiro tem mais de {{max}} linhas: divida-o.',
    'institution.classes.importReadError': 'Não foi possível ler o ficheiro.',
    'institution.classes.close': 'Fechar',
  },

  lb: {
    // Évaluations
    'institution.evaluations.title': 'Evaluatiounen',
    'institution.evaluations.subtitle': 'Resultater vun enger Klass an enger Formatioun. Ëmfroen an d’Selbstevaluatioun zielen net.',
    'institution.evaluations.subtitle_education': 'Resultater vun enger Klass an engem Cours. Ëmfroen an d’Selbstevaluatioun zielen net.',
    'institution.evaluations.loadError': 'D’Evaluatioune konnten net geluede ginn.',
    'institution.evaluations.chooseClass': 'Eng Klass wielen',
    'institution.evaluations.chooseFlow': 'Eng Formatioun wielen',
    'institution.evaluations.chooseFlow_education': 'E Cours wielen',
    'institution.evaluations.emptyClass': 'Wielt eng Klass, fir hir Resultater ze gesinn.',
    'institution.evaluations.emptyFlow': 'Dës Klass huet nach keng Formatioun gemaach.',
    'institution.evaluations.emptyFlow_education': 'Dës Klass huet nach kee Cours gemaach.',
    'institution.evaluations.emptyLearners': 'Dës Klass huet nach keng Apprenanten.',
    'institution.evaluations.emptyLearners_education': 'Dës Klass huet nach keng Schüler.',
    'institution.evaluations.viewGrid': 'Tabell',
    'institution.evaluations.viewQueue': 'Korrekturlëscht',
    'institution.evaluations.viewStats': 'Froen',
    'institution.evaluations.viewSettings': 'Reegelen',
    'institution.evaluations.viewCompetency': 'Kompetenzrahmen',
    'institution.evaluations.learner': 'Apprenant',
    'institution.evaluations.learner_education': 'Schüler',
    'institution.evaluations.total': 'Gesamt',
    'institution.evaluations.threshold': 'Schwell',
    'institution.evaluations.queueEmpty': 'Näischt ze korrigéieren.',
    'institution.evaluations.proposal': 'Virschlag',
    'institution.evaluations.comment': 'Kommentar fir den Apprenant',
    'institution.evaluations.comment_education': 'Kommentar fir de Schüler',
    'institution.evaluations.validate': 'Validéieren',
    'institution.evaluations.passPercent': 'Schwell fir ze bestoen (%)',
    'institution.evaluations.attemptRule': 'Gewäerte Versuch',
    'institution.evaluations.ruleLast': 'Leschte Versuch',
    'institution.evaluations.ruleBest': 'Beschte Versuch',
    'institution.evaluations.noSuggestion': 'Keen Niveauopstig virzeschloen.',
    'institution.evaluations.applyLevel': 'Niveauopstig iwwerhuelen',
    'institution.evaluations.openFromClass': 'D’Evaluatioune gesinn',
    'institution.evaluations.status.passed': 'Bestanen',
    'institution.evaluations.status.failed': 'Net bestanen',
    'institution.evaluations.status.toReview': 'Gëtt korrigéiert',
    'institution.evaluations.status.inProgress': 'Am Gaang',
    'institution.evaluations.status.notStarted': 'Net ugefaangen',
    'institution.evaluations.status.counted': 'Gewäert',
    'institution.evaluations.signal.easy': 'Ze einfach',
    'institution.evaluations.signal.hard': 'Ze schwéier',
    'institution.evaluations.signal.balanced': 'Equilibréiert',
    'institution.evaluations.signal.insufficient': 'Net genuch Äntwerten',
    // Classes
    'institution.classes.title': 'Klassen',
    'institution.classes.subtitle': 'Eng Klass huet e Verantwortlechen, Formateuren an Apprenanten. Importéiert d’Lëscht vun de Schüler a gitt hinnen d’Formatiounen op eemol.',
    'institution.classes.subtitle_education': 'Eng Klass huet eng Klasseléierpersoun, Enseignanten a Schüler. Importéiert d’Schülerlëscht a gitt hinnen d’Coursen op eemol.',
    'institution.classes.create': 'Eng Klass uleeën',
    'institution.classes.loadError': 'D’Klasse konnten net geluede ginn.',
    'institution.classes.saveError': 'Späicheren net méiglech.',
    'institution.classes.search': 'Eng Klass sichen…',
    'institution.classes.empty': 'Nach keng Klass. Leet déi éischt op dësem Ecran un.',
    'institution.classes.emptyFilter': 'Keng Klass entsprécht der Sich.',
    'institution.classes.colName': 'Klass',
    'institution.classes.lead': 'Klasseverantwortlechen',
    'institution.classes.lead_education': 'Klasseléierpersoun',
    'institution.classes.leadPlaceholder': 'E Klasseverantwortleche wielen',
    'institution.classes.leadPlaceholder_education': 'Eng Klasseléierpersoun wielen',
    'institution.classes.leadHint': 'De Verantwortleche suivéiert d’Klass (Klasseléierpersoun, Koordinatioun oder Direktioun). D’Formateuren enseignéieren do hir Fächer.',
    'institution.classes.leadHint_education': 'D’Klasseléierpersoun suivéiert d’Klass (oder e Koordinator, e Member vun der Direktioun). D’Enseignanten enseignéieren do hir Fächer.',
    'institution.classes.leadMissing': 'Keen Verantwortlechen',
    'institution.classes.leadMissing_education': 'Keng Klasseléierpersoun',
    'institution.classes.noLead': 'Leet fir d’éischt ënner Collaborateuren e Kont als Formateur, Formatiounsverantwortlechen oder Administrateur un.',
    'institution.classes.noLead_education': 'Leet fir d’éischt ënner Enseignanten e Kont als Enseignant, Klasseléierpersoun oder Administrateur un.',
    'institution.classes.trainers': 'Formateuren',
    'institution.classes.trainers_education': 'Enseignanten',
    'institution.classes.learners': 'Apprenanten',
    'institution.classes.learners_education': 'Schüler',
    'institution.classes.addTrainer': 'E Formateur derbäisetzen',
    'institution.classes.addTrainer_education': 'En Enseignant derbäisetzen',
    'institution.classes.addLearner': 'En Apprenant derbäisetzen',
    'institution.classes.addLearner_education': 'E Schüler derbäisetzen',
    'institution.classes.noTrainers': 'Keen Formateur an dëser Klass.',
    'institution.classes.noTrainers_education': 'Keen Enseignant an dëser Klass.',
    'institution.classes.noLearners': 'Keen Apprenant an dëser Klass.',
    'institution.classes.noLearners_education': 'Kee Schüler an dëser Klass.',
    'institution.classes.noTrainerAvailable': 'Keen Formateur disponibel.',
    'institution.classes.noTrainerAvailable_education': 'Keen Enseignant disponibel.',
    'institution.classes.noLearnerAvailable': 'Keen Apprenant disponibel.',
    'institution.classes.noLearnerAvailable_education': 'Kee Schüler disponibel.',
    'institution.classes.searchPeople': 'Sichen…',
    'institution.classes.back': 'Zréck bei d’Klassen',
    'institution.classes.remove': 'Ewechhuelen',
    'institution.classes.modalCreate': 'Eng Klass uleeën',
    'institution.classes.modalEdit': 'D’Klass änneren',
    'institution.classes.fieldName': 'Numm vun der Klass',
    'institution.classes.confirmDeleteTitle': 'Dës Klass läschen?',
    'institution.classes.confirmDeleteBody': 'D’Klass „{{name}}“ gëtt geläscht. D’Konte bleiwen erhalen.',
    'institution.classes.confirmRemoveTitle': 'Dës Persoun ewechhuelen?',
    'institution.classes.confirmRemoveBody': '{{name}} gëtt aus der Klass ewechgeholl. De Kont bleift erhalen.',
    'institution.classes.flowsTitle': 'Formatioune vun der Klass',
    'institution.classes.flowsTitle_education': 'Coursen vun der Klass',
    'institution.classes.flowsHint': 'Gitt der ganzer Klass eng publizéiert Formatioun. Schüler, déi schonn ageschriwwe sinn, ginn net duebel ugeluecht.',
    'institution.classes.flowsHint_education': 'Gitt der ganzer Klass e publizéierte Cours. Schüler, déi schonn ageschriwwe sinn, ginn net duebel ugeluecht.',
    'institution.classes.chooseFlow': 'Eng publizéiert Formatioun wielen',
    'institution.classes.chooseFlow_education': 'E publizéierte Cours wielen',
    'institution.classes.assignFlow': 'Der Klass zouweisen',
    'institution.classes.assignDone': 'Ageschriwwen: {{assigned}} · schonn ageschriwwen: {{already}}.',
    'institution.classes.noFlows': 'Keng publizéiert Formatioun fir dës Institutioun disponibel.',
    'institution.classes.noFlows_education': 'Kee publizéierte Cours fir dës Schoul disponibel.',
    'institution.classes.enrolledCount': '{{enrolled}} / {{total}} ageschriwwen',
    'institution.classes.notifyStudents': 'Déi nei ageschriwwe Schüler per E-Mail informéieren',
    'institution.classes.importCsv': 'Importéieren (CSV)',
    'institution.classes.importTitle': 'Schüler an d’Klass importéieren',
    'institution.classes.importHelp': 'CSV-Fichier mat dräi Kolonnen: Virnumm, Numm, E-Mail (Trennzeechen „;“ oder „,“). Maximal {{max}} Linnen. Feelend Konte ginn als Schülerkonten ugeluecht.',
    'institution.classes.importTemplate': 'De Modell eroflueden',
    'institution.classes.importFile': 'CSV-Fichier',
    'institution.classes.importPreview': 'Schüler aus {{file}} gelies: {{count}}.',
    'institution.classes.importPreview_one': '{{count}} Schüler aus {{file}} gelies.',
    'institution.classes.importPreview_other': '{{count}} Schüler aus {{file}} gelies.',
    'institution.classes.importSkipped': 'Linnen ouni E-Mail iwwersprongen: {{lines}}',
    'institution.classes.importInvite': 'All neie Kont eng E-Mail schécken, fir säi Passwuert ze wielen',
    'institution.classes.importRun': 'Importéieren',
    'institution.classes.importResult': 'An d’Klass derbäigesat: {{added}} · nei Konten: {{created}} · schonn an der Klass: {{already}} · Invitatiounen ënnerwee: {{invited}}.',
    'institution.classes.importErrors': 'Net importéiert Linnen: {{count}}',
    'institution.classes.importErrors_one': '{{count}} Linn net importéiert:',
    'institution.classes.importErrors_other': '{{count}} Linnen net importéiert:',
    'institution.classes.importErrorLine': 'Linn {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'Kee Schüler an dësem Fichier fonnt.',
    'institution.classes.importTooMany': 'De Fichier huet méi wéi {{max}} Linnen: deelt en op.',
    'institution.classes.importReadError': 'De Fichier konnt net gelies ginn.',
    'institution.classes.close': 'Zoumaachen',
  },

  uk: {
    // Évaluations
    'institution.evaluations.title': 'Оцінювання',
    'institution.evaluations.subtitle': 'Результати групи з навчального курсу. Опитування та самооцінювання не враховуються.',
    'institution.evaluations.subtitle_education': 'Результати класу з курсу. Опитування та самооцінювання не враховуються.',
    'institution.evaluations.loadError': 'Не вдалося завантажити оцінювання.',
    'institution.evaluations.chooseClass': 'Виберіть клас',
    'institution.evaluations.chooseFlow': 'Виберіть навчальний курс',
    'institution.evaluations.chooseFlow_education': 'Виберіть курс',
    'institution.evaluations.emptyClass': 'Виберіть клас, щоб побачити його результати.',
    'institution.evaluations.emptyFlow': 'Ця група ще не проходила жодного навчального курсу.',
    'institution.evaluations.emptyFlow_education': 'Цей клас ще не проходив жодного курсу.',
    'institution.evaluations.emptyLearners': 'У цій групі ще немає слухачів.',
    'institution.evaluations.emptyLearners_education': 'У цьому класі ще немає учнів.',
    'institution.evaluations.viewGrid': 'Таблиця',
    'institution.evaluations.viewQueue': 'Черга перевірки',
    'institution.evaluations.viewStats': 'Запитання',
    'institution.evaluations.viewSettings': 'Правила',
    'institution.evaluations.viewCompetency': 'Рамка компетентностей',
    'institution.evaluations.learner': 'Слухач',
    'institution.evaluations.learner_education': 'Учень',
    'institution.evaluations.total': 'Разом',
    'institution.evaluations.threshold': 'Поріг',
    'institution.evaluations.queueEmpty': 'Нічого перевіряти.',
    'institution.evaluations.proposal': 'Пропозиція',
    'institution.evaluations.comment': 'Коментар для слухача',
    'institution.evaluations.comment_education': 'Коментар для учня',
    'institution.evaluations.validate': 'Підтвердити',
    'institution.evaluations.passPercent': 'Прохідний поріг (%)',
    'institution.evaluations.attemptRule': 'Зарахована спроба',
    'institution.evaluations.ruleLast': 'Остання спроба',
    'institution.evaluations.ruleBest': 'Найкраща спроба',
    'institution.evaluations.noSuggestion': 'Немає підвищень рівня, які можна запропонувати.',
    'institution.evaluations.applyLevel': 'Підвищити рівень',
    'institution.evaluations.openFromClass': 'Переглянути оцінювання',
    'institution.evaluations.status.passed': 'Складено',
    'institution.evaluations.status.failed': 'Не складено',
    'institution.evaluations.status.toReview': 'На перевірці',
    'institution.evaluations.status.inProgress': 'Триває',
    'institution.evaluations.status.notStarted': 'Не розпочато',
    'institution.evaluations.status.counted': 'Зараховано',
    'institution.evaluations.signal.easy': 'Занадто легке',
    'institution.evaluations.signal.hard': 'Занадто складне',
    'institution.evaluations.signal.balanced': 'Збалансоване',
    'institution.evaluations.signal.insufficient': 'Недостатньо відповідей',
    // Classes
    'institution.classes.title': 'Класи',
    'institution.classes.subtitle': 'Клас має відповідального, тренерів і слухачів. Імпортуйте список учасників і призначте їм навчальні курси одним кроком.',
    'institution.classes.subtitle_education': 'Клас має класного керівника, вчителів і учнів. Імпортуйте список учнів і призначте їм курси одним кроком.',
    'institution.classes.create': 'Створити клас',
    'institution.classes.loadError': 'Не вдалося завантажити класи.',
    'institution.classes.saveError': 'Не вдалося зберегти.',
    'institution.classes.search': 'Пошук класу…',
    'institution.classes.empty': 'Класів ще немає. Створіть перший на цьому екрані.',
    'institution.classes.emptyFilter': 'Жоден клас не відповідає пошуку.',
    'institution.classes.colName': 'Клас',
    'institution.classes.lead': 'Відповідальний за клас',
    'institution.classes.lead_education': 'Класний керівник',
    'institution.classes.leadPlaceholder': 'Виберіть відповідального за клас',
    'institution.classes.leadPlaceholder_education': 'Виберіть класного керівника',
    'institution.classes.leadHint': 'Відповідальний супроводжує клас (класний керівник, координатор або керівництво). Тренери викладають у ньому свої предмети.',
    'institution.classes.leadHint_education': 'Класний керівник супроводжує клас (або координатор, член адміністрації). Вчителі викладають у ньому свої предмети.',
    'institution.classes.leadMissing': 'Відповідального немає',
    'institution.classes.leadMissing_education': 'Класного керівника немає',
    'institution.classes.noLead': 'Спершу створіть обліковий запис тренера, відповідального за навчання або адміністратора в розділі «Співробітники».',
    'institution.classes.noLead_education': 'Спершу створіть обліковий запис вчителя, класного керівника або адміністратора в розділі «Вчителі».',
    'institution.classes.trainers': 'Тренери',
    'institution.classes.trainers_education': 'Вчителі',
    'institution.classes.learners': 'Слухачі',
    'institution.classes.learners_education': 'Учні',
    'institution.classes.addTrainer': 'Додати тренера',
    'institution.classes.addTrainer_education': 'Додати вчителя',
    'institution.classes.addLearner': 'Додати слухача',
    'institution.classes.addLearner_education': 'Додати учня',
    'institution.classes.noTrainers': 'У цьому класі немає тренерів.',
    'institution.classes.noTrainers_education': 'У цьому класі немає вчителів.',
    'institution.classes.noLearners': 'У цьому класі немає слухачів.',
    'institution.classes.noLearners_education': 'У цьому класі немає учнів.',
    'institution.classes.noTrainerAvailable': 'Немає доступних тренерів.',
    'institution.classes.noTrainerAvailable_education': 'Немає доступних вчителів.',
    'institution.classes.noLearnerAvailable': 'Немає доступних слухачів.',
    'institution.classes.noLearnerAvailable_education': 'Немає доступних учнів.',
    'institution.classes.searchPeople': 'Пошук…',
    'institution.classes.back': 'Назад до класів',
    'institution.classes.remove': 'Вилучити',
    'institution.classes.modalCreate': 'Створити клас',
    'institution.classes.modalEdit': 'Редагувати клас',
    'institution.classes.fieldName': 'Назва класу',
    'institution.classes.confirmDeleteTitle': 'Видалити цей клас?',
    'institution.classes.confirmDeleteBody': 'Клас «{{name}}» буде видалено. Облікові записи збережуться.',
    'institution.classes.confirmRemoveTitle': 'Вилучити цю особу?',
    'institution.classes.confirmRemoveBody': '{{name}} буде вилучено з класу. Обліковий запис збережеться.',
    'institution.classes.flowsTitle': 'Навчальні курси класу',
    'institution.classes.flowsTitle_education': 'Курси класу',
    'institution.classes.flowsHint': 'Призначте опублікований навчальний курс усьому класу. Уже записані учасники не дублюються.',
    'institution.classes.flowsHint_education': 'Призначте опублікований курс усьому класу. Уже записані учні не дублюються.',
    'institution.classes.chooseFlow': 'Виберіть опублікований навчальний курс',
    'institution.classes.chooseFlow_education': 'Виберіть опублікований курс',
    'institution.classes.assignFlow': 'Призначити класу',
    'institution.classes.assignDone': 'Записано: {{assigned}} · уже були записані: {{already}}.',
    'institution.classes.noFlows': 'Для цієї установи немає опублікованих навчальних курсів.',
    'institution.classes.noFlows_education': 'Для цієї школи немає опублікованих курсів.',
    'institution.classes.enrolledCount': 'Записано {{enrolled}} / {{total}}',
    'institution.classes.notifyStudents': 'Повідомити щойно записаних учнів електронною поштою',
    'institution.classes.importCsv': 'Імпорт (CSV)',
    'institution.classes.importTitle': 'Імпорт учнів до класу',
    'institution.classes.importHelp': 'Файл CSV із трьома стовпцями: ім’я, прізвище, e-mail (роздільник «;» або «,»). Не більше {{max}} рядків. Відсутні облікові записи створюються як облікові записи учнів.',
    'institution.classes.importTemplate': 'Завантажити шаблон',
    'institution.classes.importFile': 'Файл CSV',
    'institution.classes.importPreview': 'Учнів зчитано з {{file}}: {{count}}.',
    'institution.classes.importPreview_one': 'Зчитано з {{file}}: {{count}} учень.',
    'institution.classes.importPreview_few': 'Зчитано з {{file}}: {{count}} учні.',
    'institution.classes.importPreview_many': 'Зчитано з {{file}}: {{count}} учнів.',
    'institution.classes.importPreview_other': 'Зчитано з {{file}}: {{count}} учня.',
    'institution.classes.importSkipped': 'Рядки без e-mail пропущено: {{lines}}',
    'institution.classes.importInvite': 'Надіслати кожному новому обліковому запису лист для вибору пароля',
    'institution.classes.importRun': 'Імпортувати',
    'institution.classes.importResult': 'Додано до класу: {{added}} · створено облікових записів: {{created}} · уже в класі: {{already}} · запрошень надсилається: {{invited}}.',
    'institution.classes.importErrors': 'Не імпортовано рядків: {{count}}',
    'institution.classes.importErrors_one': '{{count}} рядок не імпортовано:',
    'institution.classes.importErrors_few': '{{count}} рядки не імпортовано:',
    'institution.classes.importErrors_many': '{{count}} рядків не імпортовано:',
    'institution.classes.importErrors_other': '{{count}} рядка не імпортовано:',
    'institution.classes.importErrorLine': 'Рядок {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'У цьому файлі не знайдено жодного учня.',
    'institution.classes.importTooMany': 'Файл містить понад {{max}} рядків: розділіть його.',
    'institution.classes.importReadError': 'Не вдалося прочитати файл.',
    'institution.classes.close': 'Закрити',
  },

  ru: {
    // Évaluations
    'institution.evaluations.title': 'Оценивание',
    'institution.evaluations.subtitle': 'Результаты группы по учебному курсу. Опросы и самооценка не учитываются.',
    'institution.evaluations.subtitle_education': 'Результаты класса по курсу. Опросы и самооценка не учитываются.',
    'institution.evaluations.loadError': 'Не удалось загрузить оценивание.',
    'institution.evaluations.chooseClass': 'Выберите класс',
    'institution.evaluations.chooseFlow': 'Выберите учебный курс',
    'institution.evaluations.chooseFlow_education': 'Выберите курс',
    'institution.evaluations.emptyClass': 'Выберите класс, чтобы увидеть его результаты.',
    'institution.evaluations.emptyFlow': 'Эта группа ещё не проходила ни одного учебного курса.',
    'institution.evaluations.emptyFlow_education': 'Этот класс ещё не проходил ни одного курса.',
    'institution.evaluations.emptyLearners': 'В этой группе ещё нет учащихся.',
    'institution.evaluations.emptyLearners_education': 'В этом классе ещё нет учеников.',
    'institution.evaluations.viewGrid': 'Таблица',
    'institution.evaluations.viewQueue': 'Очередь проверки',
    'institution.evaluations.viewStats': 'Вопросы',
    'institution.evaluations.viewSettings': 'Правила',
    'institution.evaluations.viewCompetency': 'Рамка компетенций',
    'institution.evaluations.learner': 'Учащийся',
    'institution.evaluations.learner_education': 'Ученик',
    'institution.evaluations.total': 'Итого',
    'institution.evaluations.threshold': 'Порог',
    'institution.evaluations.queueEmpty': 'Проверять нечего.',
    'institution.evaluations.proposal': 'Предложение',
    'institution.evaluations.comment': 'Комментарий для учащегося',
    'institution.evaluations.comment_education': 'Комментарий для ученика',
    'institution.evaluations.validate': 'Подтвердить',
    'institution.evaluations.passPercent': 'Проходной порог (%)',
    'institution.evaluations.attemptRule': 'Засчитываемая попытка',
    'institution.evaluations.ruleLast': 'Последняя попытка',
    'institution.evaluations.ruleBest': 'Лучшая попытка',
    'institution.evaluations.noSuggestion': 'Нет повышений уровня для предложения.',
    'institution.evaluations.applyLevel': 'Повысить уровень',
    'institution.evaluations.openFromClass': 'Посмотреть оценивание',
    'institution.evaluations.status.passed': 'Сдано',
    'institution.evaluations.status.failed': 'Не сдано',
    'institution.evaluations.status.toReview': 'На проверке',
    'institution.evaluations.status.inProgress': 'В процессе',
    'institution.evaluations.status.notStarted': 'Не начато',
    'institution.evaluations.status.counted': 'Засчитано',
    'institution.evaluations.signal.easy': 'Слишком легко',
    'institution.evaluations.signal.hard': 'Слишком сложно',
    'institution.evaluations.signal.balanced': 'Сбалансировано',
    'institution.evaluations.signal.insufficient': 'Недостаточно ответов',
    // Classes
    'institution.classes.title': 'Классы',
    'institution.classes.subtitle': 'У класса есть ответственный, преподаватели и учащиеся. Импортируйте список учащихся и назначьте им учебные курсы за один раз.',
    'institution.classes.subtitle_education': 'У класса есть классный руководитель, учителя и ученики. Импортируйте список учеников и назначьте им курсы за один раз.',
    'institution.classes.create': 'Создать класс',
    'institution.classes.loadError': 'Не удалось загрузить классы.',
    'institution.classes.saveError': 'Не удалось сохранить.',
    'institution.classes.search': 'Поиск класса…',
    'institution.classes.empty': 'Классов пока нет. Создайте первый на этом экране.',
    'institution.classes.emptyFilter': 'Ни один класс не соответствует поиску.',
    'institution.classes.colName': 'Класс',
    'institution.classes.lead': 'Ответственный за класс',
    'institution.classes.lead_education': 'Классный руководитель',
    'institution.classes.leadPlaceholder': 'Выберите ответственного за класс',
    'institution.classes.leadPlaceholder_education': 'Выберите классного руководителя',
    'institution.classes.leadHint': 'Ответственный сопровождает класс (классный руководитель, координатор или руководство). Преподаватели ведут в нём свои предметы.',
    'institution.classes.leadHint_education': 'Классный руководитель сопровождает класс (или координатор, член администрации). Учителя ведут в нём свои предметы.',
    'institution.classes.leadMissing': 'Ответственного нет',
    'institution.classes.leadMissing_education': 'Классного руководителя нет',
    'institution.classes.noLead': 'Сначала создайте учётную запись преподавателя, ответственного за обучение или администратора в разделе «Сотрудники».',
    'institution.classes.noLead_education': 'Сначала создайте учётную запись учителя, классного руководителя или администратора в разделе «Учителя».',
    'institution.classes.trainers': 'Преподаватели',
    'institution.classes.trainers_education': 'Учителя',
    'institution.classes.learners': 'Учащиеся',
    'institution.classes.learners_education': 'Ученики',
    'institution.classes.addTrainer': 'Добавить преподавателя',
    'institution.classes.addTrainer_education': 'Добавить учителя',
    'institution.classes.addLearner': 'Добавить учащегося',
    'institution.classes.addLearner_education': 'Добавить ученика',
    'institution.classes.noTrainers': 'В этом классе нет преподавателей.',
    'institution.classes.noTrainers_education': 'В этом классе нет учителей.',
    'institution.classes.noLearners': 'В этом классе нет учащихся.',
    'institution.classes.noLearners_education': 'В этом классе нет учеников.',
    'institution.classes.noTrainerAvailable': 'Нет доступных преподавателей.',
    'institution.classes.noTrainerAvailable_education': 'Нет доступных учителей.',
    'institution.classes.noLearnerAvailable': 'Нет доступных учащихся.',
    'institution.classes.noLearnerAvailable_education': 'Нет доступных учеников.',
    'institution.classes.searchPeople': 'Поиск…',
    'institution.classes.back': 'Назад к классам',
    'institution.classes.remove': 'Исключить',
    'institution.classes.modalCreate': 'Создать класс',
    'institution.classes.modalEdit': 'Изменить класс',
    'institution.classes.fieldName': 'Название класса',
    'institution.classes.confirmDeleteTitle': 'Удалить этот класс?',
    'institution.classes.confirmDeleteBody': 'Класс «{{name}}» будет удалён. Учётные записи сохранятся.',
    'institution.classes.confirmRemoveTitle': 'Исключить этого человека?',
    'institution.classes.confirmRemoveBody': '{{name}} будет исключён из класса. Учётная запись сохранится.',
    'institution.classes.flowsTitle': 'Учебные курсы класса',
    'institution.classes.flowsTitle_education': 'Курсы класса',
    'institution.classes.flowsHint': 'Назначьте опубликованный учебный курс всему классу. Уже записанные учащиеся не дублируются.',
    'institution.classes.flowsHint_education': 'Назначьте опубликованный курс всему классу. Уже записанные ученики не дублируются.',
    'institution.classes.chooseFlow': 'Выберите опубликованный учебный курс',
    'institution.classes.chooseFlow_education': 'Выберите опубликованный курс',
    'institution.classes.assignFlow': 'Назначить классу',
    'institution.classes.assignDone': 'Записано: {{assigned}} · уже были записаны: {{already}}.',
    'institution.classes.noFlows': 'Для этой организации нет опубликованных учебных курсов.',
    'institution.classes.noFlows_education': 'Для этой школы нет опубликованных курсов.',
    'institution.classes.enrolledCount': 'Записано {{enrolled}} / {{total}}',
    'institution.classes.notifyStudents': 'Уведомить только что записанных учеников по электронной почте',
    'institution.classes.importCsv': 'Импорт (CSV)',
    'institution.classes.importTitle': 'Импорт учеников в класс',
    'institution.classes.importHelp': 'Файл CSV с тремя столбцами: имя, фамилия, e-mail (разделитель «;» или «,»). Не более {{max}} строк. Недостающие учётные записи создаются как учётные записи учеников.',
    'institution.classes.importTemplate': 'Скачать шаблон',
    'institution.classes.importFile': 'Файл CSV',
    'institution.classes.importPreview': 'Учеников прочитано из {{file}}: {{count}}.',
    'institution.classes.importPreview_one': 'Прочитано из {{file}}: {{count}} ученик.',
    'institution.classes.importPreview_few': 'Прочитано из {{file}}: {{count}} ученика.',
    'institution.classes.importPreview_many': 'Прочитано из {{file}}: {{count}} учеников.',
    'institution.classes.importPreview_other': 'Прочитано из {{file}}: {{count}} ученика.',
    'institution.classes.importSkipped': 'Строки без e-mail пропущены: {{lines}}',
    'institution.classes.importInvite': 'Отправить каждой новой учётной записи письмо для выбора пароля',
    'institution.classes.importRun': 'Импортировать',
    'institution.classes.importResult': 'Добавлено в класс: {{added}} · создано учётных записей: {{created}} · уже в классе: {{already}} · отправляется приглашений: {{invited}}.',
    'institution.classes.importErrors': 'Не импортировано строк: {{count}}',
    'institution.classes.importErrors_one': '{{count}} строка не импортирована:',
    'institution.classes.importErrors_few': '{{count}} строки не импортированы:',
    'institution.classes.importErrors_many': '{{count}} строк не импортировано:',
    'institution.classes.importErrors_other': '{{count}} строки не импортировано:',
    'institution.classes.importErrorLine': 'Строка {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'В этом файле не найдено ни одного ученика.',
    'institution.classes.importTooMany': 'Файл содержит более {{max}} строк: разделите его.',
    'institution.classes.importReadError': 'Не удалось прочитать файл.',
    'institution.classes.close': 'Закрыть',
  },

  // Arabe : les clés de base sont déjà dans `i18n-ar-education.json` (appliqué en dernier) ;
  // ici, la variante scolaire `_education` et les pluriels.
  ar: {
    'institution.evaluations.subtitle_education': 'نتائج القسم في درس ما. لا تُحتسب استطلاعات الرأي والتقييم الذاتي.',
    'institution.evaluations.chooseFlow_education': 'اختيار درس',
    'institution.evaluations.emptyFlow_education': 'لم يُتابع هذا القسم أي درس بعد.',
    'institution.evaluations.emptyLearners_education': 'لا يضم هذا القسم أي تلاميذ بعد.',
    'institution.evaluations.learner_education': 'التلميذ',
    'institution.evaluations.comment_education': 'تعليق للتلميذ',
    'institution.classes.subtitle_education': 'يضم القسم معلمًا رئيسيًا ومعلمين وتلاميذ. استورد قائمة التلاميذ وعيّن لهم الدروس دفعة واحدة.',
    'institution.classes.lead_education': 'المعلم الرئيسي',
    'institution.classes.leadPlaceholder_education': 'اختيار المعلم الرئيسي',
    'institution.classes.leadHint_education': 'يتابع المعلم الرئيسي القسم (أو منسق أو عضو في الإدارة). ويدرّس المعلمون موادهم فيه.',
    'institution.classes.leadMissing_education': 'لا يوجد معلم رئيسي',
    'institution.classes.noLead_education': 'أنشئ أولًا حساب معلم أو معلم رئيسي أو مدير في قسم المعلمين.',
    'institution.classes.trainers_education': 'المعلمون',
    'institution.classes.learners_education': 'التلاميذ',
    'institution.classes.addTrainer_education': 'إضافة معلم',
    'institution.classes.addLearner_education': 'إضافة تلميذ',
    'institution.classes.noTrainers_education': 'لا يوجد معلم في هذا القسم.',
    'institution.classes.noLearners_education': 'لا يوجد تلميذ في هذا القسم.',
    'institution.classes.noTrainerAvailable_education': 'لا يوجد معلم متاح.',
    'institution.classes.noLearnerAvailable_education': 'لا يوجد تلميذ متاح.',
    'institution.classes.flowsTitle_education': 'دروس القسم',
    'institution.classes.flowsHint_education': 'عيّن درسًا منشورًا للقسم بأكمله. لن يتكرر تسجيل التلاميذ المسجلين مسبقًا.',
    'institution.classes.chooseFlow_education': 'اختيار درس منشور',
    'institution.classes.noFlows_education': 'لا يوجد درس منشور متاح لهذه المؤسسة.',
    'institution.classes.importPreview_zero': 'لم تتم قراءة أي تلميذ من {{file}}.',
    'institution.classes.importPreview_one': 'تمت قراءة تلميذ واحد من {{file}}.',
    'institution.classes.importPreview_two': 'تمت قراءة تلميذَين من {{file}}.',
    'institution.classes.importPreview_few': 'تمت قراءة {{count}} تلاميذ من {{file}}.',
    'institution.classes.importPreview_many': 'تمت قراءة {{count}} تلميذًا من {{file}}.',
    'institution.classes.importPreview_other': 'تمت قراءة {{count}} تلميذ من {{file}}.',
    'institution.classes.importErrors_zero': 'لا توجد أسطر غير مستوردة:',
    'institution.classes.importErrors_one': 'سطر واحد لم يُستورد:',
    'institution.classes.importErrors_two': 'سطران لم يُستوردا:',
    'institution.classes.importErrors_few': '{{count}} أسطر لم تُستورد:',
    'institution.classes.importErrors_many': '{{count}} سطرًا لم يُستورد:',
    'institution.classes.importErrors_other': '{{count}} سطر لم يُستورد:',
  },

  tr: {
    'institution.common.add': 'Ekle',
    'institution.common.cancel': 'İptal',
    'institution.common.delete': 'Sil',
    'institution.common.edit': 'Düzenle',
    'institution.common.refresh': 'Yenile',
    'institution.common.retry': 'Yeniden dene',
    'institution.common.save': 'Kaydet',
    'institution.common.actions': 'İşlemler',
    // Évaluations
    'institution.evaluations.title': 'Değerlendirmeler',
    'institution.evaluations.subtitle': 'Bir sınıfın bir eğitimdeki sonuçları. Anketler ve öz değerlendirme sayılmaz.',
    'institution.evaluations.subtitle_education': 'Bir sınıfın bir dersteki sonuçları. Anketler ve öz değerlendirme sayılmaz.',
    'institution.evaluations.loadError': 'Değerlendirmeler yüklenemedi.',
    'institution.evaluations.chooseClass': 'Sınıf seçin',
    'institution.evaluations.chooseFlow': 'Eğitim seçin',
    'institution.evaluations.chooseFlow_education': 'Ders seçin',
    'institution.evaluations.emptyClass': 'Sonuçlarını görmek için bir sınıf seçin.',
    'institution.evaluations.emptyFlow': 'Bu sınıf henüz hiçbir eğitimi oynamadı.',
    'institution.evaluations.emptyFlow_education': 'Bu sınıf henüz hiçbir dersi işlemedi.',
    'institution.evaluations.emptyLearners': 'Bu sınıfta henüz katılımcı yok.',
    'institution.evaluations.emptyLearners_education': 'Bu sınıfta henüz öğrenci yok.',
    'institution.evaluations.viewGrid': 'Tablo',
    'institution.evaluations.viewQueue': 'Değerlendirme sırası',
    'institution.evaluations.viewStats': 'Sorular',
    'institution.evaluations.viewSettings': 'Kurallar',
    'institution.evaluations.viewCompetency': 'Yetkinlik çerçevesi',
    'institution.evaluations.learner': 'Katılımcı',
    'institution.evaluations.learner_education': 'Öğrenci',
    'institution.evaluations.total': 'Toplam',
    'institution.evaluations.threshold': 'Eşik',
    'institution.evaluations.queueEmpty': 'Değerlendirilecek bir şey yok.',
    'institution.evaluations.proposal': 'Öneri',
    'institution.evaluations.comment': 'Katılımcıya yorum',
    'institution.evaluations.comment_education': 'Öğrenciye yorum',
    'institution.evaluations.validate': 'Onayla',
    'institution.evaluations.passPercent': 'Geçme eşiği (%)',
    'institution.evaluations.attemptRule': 'Dikkate alınan deneme',
    'institution.evaluations.ruleLast': 'Son deneme',
    'institution.evaluations.ruleBest': 'En iyi deneme',
    'institution.evaluations.noSuggestion': 'Önerilecek seviye yükseltmesi yok.',
    'institution.evaluations.applyLevel': 'Seviyeyi yükselt',
    'institution.evaluations.openFromClass': 'Değerlendirmeleri gör',
    'institution.evaluations.status.passed': 'Geçti',
    'institution.evaluations.status.failed': 'Geçemedi',
    'institution.evaluations.status.toReview': 'Değerlendiriliyor',
    'institution.evaluations.status.inProgress': 'Devam ediyor',
    'institution.evaluations.status.notStarted': 'Başlamadı',
    'institution.evaluations.status.counted': 'Sayıldı',
    'institution.evaluations.signal.easy': 'Çok kolay',
    'institution.evaluations.signal.hard': 'Çok zor',
    'institution.evaluations.signal.balanced': 'Dengeli',
    'institution.evaluations.signal.insufficient': 'Yeterli yanıt yok',
    // Classes
    'institution.classes.title': 'Sınıflar',
    'institution.classes.subtitle': 'Bir sınıfın bir sorumlusu, eğitmenleri ve katılımcıları vardır. Öğrenci listesini içe aktarın ve eğitimleri hepsine tek seferde atayın.',
    'institution.classes.subtitle_education': 'Bir sınıfın bir sınıf öğretmeni, öğretmenleri ve öğrencileri vardır. Öğrenci listesini içe aktarın ve dersleri hepsine tek seferde atayın.',
    'institution.classes.create': 'Sınıf oluştur',
    'institution.classes.loadError': 'Sınıflar yüklenemedi.',
    'institution.classes.saveError': 'Kaydedilemedi.',
    'institution.classes.search': 'Sınıf ara…',
    'institution.classes.empty': 'Henüz sınıf yok. İlkini bu ekrandan oluşturun.',
    'institution.classes.emptyFilter': 'Aramayla eşleşen sınıf yok.',
    'institution.classes.colName': 'Sınıf',
    'institution.classes.lead': 'Sınıf sorumlusu',
    'institution.classes.lead_education': 'Sınıf öğretmeni',
    'institution.classes.leadPlaceholder': 'Sınıf sorumlusu seçin',
    'institution.classes.leadPlaceholder_education': 'Sınıf öğretmeni seçin',
    'institution.classes.leadHint': 'Sorumlu kişi sınıfı takip eder (sınıf öğretmeni, koordinatör veya yönetim). Eğitmenler bu sınıfta kendi derslerini verir.',
    'institution.classes.leadHint_education': 'Sınıf öğretmeni sınıfı takip eder (ya da bir koordinatör, okul yönetiminden biri). Öğretmenler bu sınıfta kendi derslerini verir.',
    'institution.classes.leadMissing': 'Sorumlu yok',
    'institution.classes.leadMissing_education': 'Sınıf öğretmeni yok',
    'institution.classes.noLead': 'Önce Çalışanlar bölümünde bir eğitmen, eğitim sorumlusu veya yönetici hesabı oluşturun.',
    'institution.classes.noLead_education': 'Önce Öğretmenler bölümünde bir öğretmen, sınıf öğretmeni veya yönetici hesabı oluşturun.',
    'institution.classes.trainers': 'Eğitmenler',
    'institution.classes.trainers_education': 'Öğretmenler',
    'institution.classes.learners': 'Katılımcılar',
    'institution.classes.learners_education': 'Öğrenciler',
    'institution.classes.addTrainer': 'Eğitmen ekle',
    'institution.classes.addTrainer_education': 'Öğretmen ekle',
    'institution.classes.addLearner': 'Katılımcı ekle',
    'institution.classes.addLearner_education': 'Öğrenci ekle',
    'institution.classes.noTrainers': 'Bu sınıfta eğitmen yok.',
    'institution.classes.noTrainers_education': 'Bu sınıfta öğretmen yok.',
    'institution.classes.noLearners': 'Bu sınıfta katılımcı yok.',
    'institution.classes.noLearners_education': 'Bu sınıfta öğrenci yok.',
    'institution.classes.noTrainerAvailable': 'Uygun eğitmen yok.',
    'institution.classes.noTrainerAvailable_education': 'Uygun öğretmen yok.',
    'institution.classes.noLearnerAvailable': 'Uygun katılımcı yok.',
    'institution.classes.noLearnerAvailable_education': 'Uygun öğrenci yok.',
    'institution.classes.searchPeople': 'Ara…',
    'institution.classes.back': 'Sınıflara dön',
    'institution.classes.remove': 'Çıkar',
    'institution.classes.modalCreate': 'Sınıf oluştur',
    'institution.classes.modalEdit': 'Sınıfı düzenle',
    'institution.classes.fieldName': 'Sınıf adı',
    'institution.classes.confirmDeleteTitle': 'Bu sınıf silinsin mi?',
    'institution.classes.confirmDeleteBody': '“{{name}}” sınıfı silinecek. Hesaplar korunur.',
    'institution.classes.confirmRemoveTitle': 'Bu kişi çıkarılsın mı?',
    'institution.classes.confirmRemoveBody': '{{name}} sınıftan çıkarılacak. Hesap korunur.',
    'institution.classes.flowsTitle': 'Sınıfın eğitimleri',
    'institution.classes.flowsTitle_education': 'Sınıfın dersleri',
    'institution.classes.flowsHint': 'Yayımlanmış bir eğitimi tüm sınıfa atayın. Zaten kayıtlı olan öğrenciler tekrar eklenmez.',
    'institution.classes.flowsHint_education': 'Yayımlanmış bir dersi tüm sınıfa atayın. Zaten kayıtlı olan öğrenciler tekrar eklenmez.',
    'institution.classes.chooseFlow': 'Yayımlanmış bir eğitim seçin',
    'institution.classes.chooseFlow_education': 'Yayımlanmış bir ders seçin',
    'institution.classes.assignFlow': 'Sınıfa ata',
    'institution.classes.assignDone': 'Kaydedilen öğrenci: {{assigned}} · zaten kayıtlı: {{already}}.',
    'institution.classes.noFlows': 'Bu kurum için yayımlanmış eğitim yok.',
    'institution.classes.noFlows_education': 'Bu okul için yayımlanmış ders yok.',
    'institution.classes.enrolledCount': '{{enrolled}} / {{total}} kayıtlı',
    'institution.classes.notifyStudents': 'Yeni kaydolan öğrencileri e-postayla bilgilendir',
    'institution.classes.importCsv': 'İçe aktar (CSV)',
    'institution.classes.importTitle': 'Sınıfa öğrenci aktar',
    'institution.classes.importHelp': 'Üç sütunlu CSV dosyası: ad, soyad, e-posta (ayraç ";" veya ","). En fazla {{max}} satır. Eksik hesaplar öğrenci hesabı olarak oluşturulur.',
    'institution.classes.importTemplate': 'Şablonu indir',
    'institution.classes.importFile': 'CSV dosyası',
    'institution.classes.importPreview': '{{file}} dosyasından okunan öğrenci: {{count}}.',
    'institution.classes.importPreview_one': '{{file}} dosyasından {{count}} öğrenci okundu.',
    'institution.classes.importPreview_other': '{{file}} dosyasından {{count}} öğrenci okundu.',
    'institution.classes.importSkipped': 'E-postası olmadığı için atlanan satırlar: {{lines}}',
    'institution.classes.importInvite': 'Her yeni hesaba şifresini belirlemesi için bir e-posta gönder',
    'institution.classes.importRun': 'İçe aktar',
    'institution.classes.importResult': 'Sınıfa eklenen: {{added}} · oluşturulan hesap: {{created}} · zaten sınıfta: {{already}} · gönderilmekte olan davet: {{invited}}.',
    'institution.classes.importErrors': 'İçe aktarılmayan satır: {{count}}',
    'institution.classes.importErrors_one': '{{count}} satır içe aktarılmadı:',
    'institution.classes.importErrors_other': '{{count}} satır içe aktarılmadı:',
    'institution.classes.importErrorLine': 'Satır {{line}} ({{email}}): {{reason}}',
    'institution.classes.importEmpty': 'Bu dosyada öğrenci bulunamadı.',
    'institution.classes.importTooMany': 'Dosya {{max}} satırı aşıyor: lütfen bölün.',
    'institution.classes.importReadError': 'Dosya okunamadı.',
    'institution.classes.close': 'Kapat',
  },
};
